import { createStore, applyMiddleware, combineReducers, compose, Reducer } from 'redux';
import { createDevLoggerRelay } from './services/devLogger';
import { composeWithDevTools } from '@redux-devtools/extension';
import { translateService } from './services/TranslateService';
import SecureLS from 'secure-ls';
import { ResponseModalStruct } from './interfaces/ModalStructs';
import ApiService from './services/api/ApiService';
import * as Sentry from "@sentry/react";

// structure of entire Redux store
export interface GlobalStore {
    chauffeurReducer: {
        dbToken: string | undefined;
        jwt: string | undefined;
        deviceLanguage: string;
        API_VERSION: string;
        API_URL: string;
        WALLEY_API_URL: string;
        responseModal: ResponseModalStruct | undefined;
        apiMessage: string | undefined;
        apiService: ApiService | undefined;
    };
}

// each of the actions we use
type ActionApiUrl = {
    type: 'SET_API_URL';
    payload: string;
};
type ActionWalleyApiUrl = {
    type: 'SET_WALLEY_API_URL';
    payload: string;
};
type ActionApiVer = {
    type: 'SET_API_VERSION';
    payload: string;
};
type ActionLang = {
    type: 'SET_LANG';
    payload: string;
};
type ActionToken = {
    type: 'SET_TOKEN';
    payload: {
        token: string;
        jwt: string;
        driverLogin: boolean;
    };
};
type ActionClearToken = {
    type: 'CLEAR_TOKEN';
};
type ActionGetToken = {
    type: 'GET_TOKEN';
};
type ActionResModal = {
    type: 'SET_RESPONSE_MODAL';
    payload: any;
};
type ActionApiMessage = {
    type: 'SET_API_MESSAGE';
    payload: string | undefined;
};
type ActionSetApiService = {
    type: 'SET_API_SERVICE';
    payload: ApiService;
};

type ActionSetClearSelections = {
    type: 'CLEAR_SELECTIONS';
    payload: any ;
};

// all the Actions merged, and exported as Dispatch
type Action =
    | ActionApiUrl
    | ActionWalleyApiUrl
    | ActionApiVer
    | ActionLang
    | ActionToken
    | ActionGetToken
    | ActionClearToken
    | ActionResModal
    | ActionApiMessage
    | ActionSetApiService
    | ActionSetClearSelections

export type Dispatch = (action: Action) => void;

const aesC = new SecureLS({ encodingType: 'aes', encryptionSecret: import.meta.env.VITE_TSEC });

// initial state of the store
const INITIAL_STATE: GlobalStore = {
    chauffeurReducer: {
        dbToken: '',
        jwt: '',
        deviceLanguage: 'fi',
        API_VERSION: '2',
        API_URL: '',
        WALLEY_API_URL: '',
        responseModal: undefined,
        apiMessage: undefined,
        apiService: undefined,
    },
};

const mainReducer: Reducer = (state = INITIAL_STATE.chauffeurReducer, action: Action) => {
    switch (action.type) {
        case 'SET_API_VERSION':
            return {
                ...state,
                API_VERSION: action.payload,
            };

        case 'SET_API_URL':
            return {
                ...state,
                API_URL: action.payload,
            };

        case 'SET_WALLEY_API_URL':
            return {
                ...state,
                WALLEY_API_URL: action.payload,
            };

        case 'SET_LANG':
            // is this a side-effect??
            translateService.setLocale(action.payload);
            return {
                ...state,
                deviceLanguage: action.payload,
            };

        case 'CLEAR_TOKEN':
            aesC.remove('C2-XT');
            aesC.remove('C2-STORE');
            return {
                ...state,
                dbToken: INITIAL_STATE.chauffeurReducer.dbToken,
                jwt: INITIAL_STATE.chauffeurReducer.jwt,
            };

        case 'SET_TOKEN':
            aesC.set('C2-XT', action.payload.token);
            aesC.set('C2-STORE', action.payload.jwt);
            localStorage.setItem('C2-DL', action.payload.driverLogin ? '1' : '0');
            return {
                ...state,
                dbToken: action.payload.token,
                jwt: action.payload.jwt,
                driverLogin: action.payload.driverLogin,
            };

        case 'GET_TOKEN':{
            try {
                return {
                    ...state,
                    dbToken: aesC.get('C2-XT'),
                    jwt: aesC.get('C2-STORE'),
                };
            } catch(e) {
                aesC.remove('C2-XT');
                aesC.remove('C2-STORE');
                return {
                    ...state,
                    dbToken: '',
                    jwt: '',
                }
            }
        }

        case 'SET_RESPONSE_MODAL':
            if (action.payload) {
                return {
                    ...state,
                    responseModal: action.payload,
                };
            } else {
                return {
                    ...state,
                    responseModal: undefined,
                };
            }

        case 'SET_API_MESSAGE':
            if (action.payload) {
                return {
                    ...state,
                    apiMessage: action.payload,
                };
            } else {
                return {
                    ...state,
                    apiMessage: undefined,
                };
            }

        case 'SET_API_SERVICE':
            return {
                ...state,
                apiService: action.payload,
            };

        case 'CLEAR_SELECTIONS':
            sessionStorage.removeItem("coupon");
            sessionStorage.removeItem("page");
            sessionStorage.removeItem("time");
            sessionStorage.removeItem("reservationCreated");
            return {
                ...state,
                selectedCar: null,
            };

        default:
            return state;
    }
};

const sentryReduxEnhancer = Sentry.createReduxEnhancer({});

// Action logging for development only — `vite dev` on a host outside 24rent.fi. The logger used
// to be a static import gated at runtime by hostname, which kept redux-logger in every
// production bundle for a branch that never ran there (row I2). Now the production build has
// no logger at all; in development it is fetched after the store exists and forwards actions
// from then on (the store must be created synchronously, so the middleware is a thin relay).
const devLogging = import.meta.env.DEV && window.location.hostname.toString().indexOf('24rent.fi') === -1;
const devLogger = devLogging ? createDevLoggerRelay() : undefined;
const middleware: any = devLogger ? applyMiddleware(devLogger.middleware) : null;
if (devLogger) {
    import('redux-logger').then(module => devLogger.attach(module)).catch(() => { /* no logging, nothing else changes */ });
}

const combinedReducer = combineReducers({
    chauffeurReducer: mainReducer,
});



export const store = createStore(combinedReducer, compose(
    sentryReduxEnhancer, composeWithDevTools(middleware)) as any);
